import React from 'react'
import { cls } from '@utils/classnames'

import { PageCounterAriaLabel } from '../ProductExplorerTypes'

import { getPaginationUrl } from './paginationUrl'
import { DOTS, usePagination } from './usePagination'

interface PageCounterProperties {
  totalElements: number
  reference: React.MutableRefObject<any>
  currentPage: number
  maxDisplayedElementCount: number
  pageSize: number
  onPageChange: (newValue: number) => void
  areaLabels?: PageCounterAriaLabel
}

const PageCounter: React.FC<PageCounterProperties> = ({
  totalElements,
  currentPage,
  reference,
  maxDisplayedElementCount,
  pageSize,
  onPageChange,
  areaLabels,
}: PageCounterProperties) => {
  const paginationRange = usePagination(totalElements, pageSize, maxDisplayedElementCount, currentPage)
  const lastPage = paginationRange[paginationRange.length - 1]
  const effectiveBaseUrl = `${window.location.pathname}${window.location.search}`

  function scrollToReference() {
    reference?.current?.scrollIntoView()
  }

  function onNext(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    event.preventDefault()
    if (currentPage !== lastPage) {
      onPageChange(currentPage + 1)
      scrollToReference()
    }
  }

  function onPrevious(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    event.preventDefault()
    if (currentPage !== 1) {
      onPageChange(currentPage - 1)
      scrollToReference()
    }
  }

  return paginationRange?.length > 1 ? (
    <nav
      aria-label={areaLabels?.paginationAriaLabel
        ?.replace('{0}', currentPage.toString())
        .replace('{1}', lastPage.toString())}
    >
      <ul className="cmp-page-counter">
        <li
          className={cls({
            'cmp-page-counter__item': true,
            'cmp-page-counter__item--disabled': currentPage === 1,
          })}
        >
          <a
            href={getPaginationUrl(effectiveBaseUrl, currentPage - 1)}
            onClick={onPrevious}
            className="cmp-page-counter__page-link"
          >
            <div className="cmp-page-counter__item--arrow-left" aria-hidden="true" />
            <span className="screen-reader-text">{areaLabels?.paginationScreenReaderTextPreviousPage}</span>
          </a>
        </li>
        {paginationRange.map((pageNumber, index) => {
          if (pageNumber === DOTS) {
            return (
              <li className="cmp-page-counter__item cmp-page-counter__item--dots" key={index}>
                ..
              </li>
            )
          }

          const isCurrentPage = pageNumber === currentPage

          return (
            <li
              className={cls({
                'cmp-page-counter__item': true,
                'cmp-page-counter__item--selected': isCurrentPage,
              })}
              key={index}
            >
              <a
                href={getPaginationUrl(effectiveBaseUrl, pageNumber)}
                onClick={(event) => {
                  event.preventDefault()
                  onPageChange(pageNumber)
                  scrollToReference()
                }}
                aria-current={isCurrentPage ? 'page' : undefined}
                className="cmp-page-counter__page-link"
              >
                <span className="screen-reader-text">{areaLabels?.paginationScreenReaderTextGoToPage}</span>
                {pageNumber}
              </a>
            </li>
          )
        })}
        <li
          className={cls({
            'cmp-page-counter__item': true,
            'cmp-page-counter__item--disabled': currentPage === lastPage,
          })}
        >
          <a
            href={getPaginationUrl(effectiveBaseUrl, currentPage + 1)}
            onClick={onNext}
            className="cmp-page-counter__page-link"
          >
            <div className="cmp-page-counter__item--arrow-right" aria-hidden="true" />
            <span className="screen-reader-text">{areaLabels?.paginationScreenReaderTextNextPage}</span>
          </a>
        </li>
      </ul>
    </nav>
  ) : null
}

export default PageCounter
